import { useLicenseStore } from '../store/licenseStore';
import {
  useSubscriptionStore,
  selectIsPro as selectIsSubscriptionPro,
  selectIsInGracePeriod,
  selectGracePeriodDaysRemaining,
} from '../store/subscriptionStore';
import { isBrowserMode } from '../../../features/browser-mode/browserModeUtils';
import { useHasProAccess } from './useHasProAccess';

/**
 * Footer visibility mode:
 * - 'hidden': Footer should not be shown
 * - 'personal': Show "For personal, non-commercial use only" message
 * - 'trial': Show "Pro Trial - X days left" message
 * - 'grace-period': Show subscription expired countdown (future: when subscription expires)
 */
export type FooterMode = 'hidden' | 'personal' | 'trial' | 'grace-period';

export interface FooterVisibility {
  /** Whether the footer should be visible */
  visible: boolean;
  /** The mode determines what content to show */
  mode: FooterMode;
  /** Days remaining in trial (only for 'trial' mode) */
  trialDaysRemaining?: number;
  /** Days remaining in grace period (only for 'grace-period' mode) */
  gracePeriodDays?: number;
}

/**
 * Hook to determine Personal Mode footer visibility.
 *
 * Footer visibility rules:
 * - Hidden: active subscription (Pro mode), browser mode, dev Pro mode
 * - Visible (trial): active trial - show "Pro Trial - X days left"
 * - Visible (personal): trial skipped, trial never started, trial expired without subscription
 * - Visible (grace-period): subscription expired but within 7-day grace period
 *
 * Note: Browser mode always hides the footer because it provides a full Pro experience.
 */
export function useFooterVisibility(): FooterVisibility {
  const getTrialState = useLicenseStore((s) => s.getTrialState);
  const getTrialDaysRemaining = useLicenseStore((s) => s.getTrialDaysRemaining);

  // Reactive Pro access (correctly subscribes to all sources)
  const hasProAccess = useHasProAccess();

  // Subscription-derived entitlement (reactive) - needed for grace period display
  const isSubscriptionPro = useSubscriptionStore(selectIsSubscriptionPro);
  const isInGracePeriod = useSubscriptionStore(selectIsInGracePeriod);
  const gracePeriodDaysRemaining = useSubscriptionStore(selectGracePeriodDaysRemaining);

  // Browser mode: never show personal mode footer - users get Pro experience
  if (isBrowserMode()) {
    return { visible: false, mode: 'hidden' };
  }

  const trialState = getTrialState();

  // Hide footer for active subscription
  if (isSubscriptionPro) {
    return { visible: false, mode: 'hidden' };
  }

  // Active trial: show trial footer with days remaining
  if (trialState === 'active') {
    return {
      visible: true,
      mode: 'trial',
      trialDaysRemaining: getTrialDaysRemaining(),
    };
  }

  // Grace period: show countdown footer variant
  if (isInGracePeriod) {
    return {
      visible: true,
      mode: 'grace-period',
      gracePeriodDays: gracePeriodDaysRemaining,
    };
  }

  // Dev/License Pro (not in trial): hide footer
  // Use hasProAccess to catch dev mode and other Pro grants
  if (hasProAccess) {
    return { visible: false, mode: 'hidden' };
  }

  // Personal mode states: show footer
  // - 'not-started': never started trial
  // - 'skipped': chose Personal mode over trial
  // - 'expired': trial expired without subscription
  return { visible: true, mode: 'personal' };
}
